(() => {
  if (window.DailyXMobile) return;
  const mobileQuery = window.matchMedia('(max-width: 800px)');
  const panelSelector = '[data-dx-mobile-panel], [data-dx-mobile-search-sheet], [data-dx-filter-panel]';
  let activePanel = null;
  let returnFocus = null;

  const panels = () => [...document.querySelectorAll(panelSelector)];
  const visibleFocusable = (panel) => [...panel.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
  )].filter((element) => element.getClientRects().length && !element.closest('[inert]'));

  function closePanel(panel) {
    if (panel.matches('[data-dx-mobile-panel]') && window.DailyXTheme?.setMenu) {
      window.DailyXTheme.setMenu(false);
    } else {
      panel.classList.remove('is-open');
    }
    panel.querySelectorAll('details[open]').forEach((details) => { details.open = false; });
  }

  function sync({ focus = false, restore = true } = {}) {
    const openPanel = mobileQuery.matches ? panels().find((panel) => panel.classList.contains('is-open')) : null;
    panels().forEach((panel) => {
      const isDesktopFilter = !mobileQuery.matches && panel.matches('[data-dx-filter-panel]');
      const isOpen = panel === openPanel;
      panel.inert = !isOpen && !isDesktopFilter;
      if (isDesktopFilter) {
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('aria-modal');
        panel.removeAttribute('role');
      } else {
        panel.setAttribute('aria-hidden', String(!isOpen));
        if (panel.matches('[data-dx-filter-panel]')) {
          panel.setAttribute('role', 'dialog');
          panel.setAttribute('aria-modal', 'true');
          panel.setAttribute('tabindex', '-1');
        }
      }
      document.querySelectorAll('[aria-controls]').forEach((button) => {
        if (button.getAttribute('aria-controls') === panel.id) button.setAttribute('aria-expanded', String(isOpen));
      });
    });
    const backdrop = document.querySelector('[data-dx-app-backdrop]');
    if (backdrop) backdrop.hidden = !openPanel;
    document.body.classList.toggle('dx-app-lock', Boolean(openPanel));
    document.body.style.overflow = openPanel ? 'hidden' : '';

    const previousPanel = activePanel;
    activePanel = openPanel || null;
    if (openPanel && focus && previousPanel !== openPanel) {
      requestAnimationFrame(() => {
        if (!openPanel.isConnected || openPanel !== activePanel) return;
        const target = openPanel.querySelector('input[type="search"]') || visibleFocusable(openPanel)[0] || openPanel;
        target.focus({ preventScroll: true });
      });
    } else if (!openPanel && previousPanel && restore) {
      if (returnFocus?.isConnected && returnFocus.getClientRects().length) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    }
  }

  function closeAll({ restore = true } = {}) {
    panels().forEach(closePanel);
    sync({ restore });
    if (!restore) returnFocus = null;
  }

  function openPanel(panel, trigger) {
    if (!panel || !mobileQuery.matches) return;
    if (!activePanel?.contains(trigger)) returnFocus = trigger;
    panels().forEach((candidate) => { if (candidate !== panel) closePanel(candidate); });
    panel.classList.add('is-open');
    sync({ focus: true, restore: false });
  }

  function init() {
    // Query fresh markup on every operation; theme editor and filter rendering can replace sections.
    closeAll({ restore: false });
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const searchButton = event.target.closest('[data-dx-mobile-search-toggle]');
    const filterButton = event.target.closest('[data-dx-filter-toggle]');
    const menuButton = event.target.closest('[data-dx-menu-toggle]');

    if (searchButton) {
      openPanel(document.querySelector('[data-dx-mobile-search-sheet]'), searchButton);
    } else if (filterButton) {
      const filter = document.getElementById(filterButton.getAttribute('aria-controls'));
      if (filter?.classList.contains('is-open')) closeAll();
      else openPanel(filter, filterButton);
    } else if (menuButton) {
      // dailyx.js owns the menu toggle. Synchronize after that one delegated handler runs.
      requestAnimationFrame(() => {
        const menu = document.querySelector('[data-dx-mobile-panel]');
        if (menu?.classList.contains('is-open')) {
          if (!activePanel?.contains(menuButton)) returnFocus = menuButton;
          panels().forEach((panel) => { if (panel !== menu) closePanel(panel); });
        }
        sync({ focus: true });
      });
    } else if (event.target.closest('[data-dx-mobile-search-close], [data-dx-app-backdrop]')) {
      closeAll();
    } else if (activePanel && event.target.closest('a[href]') && activePanel.contains(event.target)) {
      closeAll({ restore: false });
    }

    if (!event.target.closest('.dx-header__nav')) {
      document.querySelectorAll('.dx-header__nav details[open]').forEach((details) => { details.open = false; });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (activePanel) {
        event.preventDefault();
        closeAll();
      } else {
        const disclosure = document.activeElement?.closest('.dx-header__nav details[open]');
        if (disclosure) {
          disclosure.open = false;
          disclosure.querySelector('summary')?.focus();
        }
      }
      return;
    }
    if (event.key !== 'Tab' || !activePanel) return;
    const focusable = visibleFocusable(activePanel);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      activePanel.focus();
    } else if (!activePanel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('toggle', (event) => {
    const disclosure = event.target;
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.open || !disclosure.matches('.dx-nav .dx-menu__disclosure')) return;
    document.querySelectorAll('.dx-nav .dx-menu__disclosure[open]').forEach((sibling) => {
      if (sibling !== disclosure) sibling.open = false;
    });
  }, true);

  mobileQuery.addEventListener('change', () => closeAll({ restore: false }));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => closeAll({ restore: false }));
  window.DailyXMobile = { init, closeAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
